import { useTranslation } from 'react-i18next';
import type { ProgramListingItemGroup } from '../ProgramListing/utils';
import type { AssessmentRunsFormProps } from '../shared/AssessmentRunsFormContainer';
import AssessmentRunsFormContainer from '../shared/AssessmentRunsFormContainer';
import { UpdateAssessmentRunsMutation } from './UpdateAssessmentRuns.graphql';
import type { UpdateAssessmentRunsMutationVariables } from './UpdateAssessmentRuns.graphql';
import client from '@/apolloClient';
import type { ProgramListItemDataFragment } from '@/gql/graphql';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

export interface UpdateAssessmentRunsProps {
    program: ProgramListItemDataFragment;
    selectedGroup?: ProgramListingItemGroup | null;
    onClose: () => void;
    onSuccess: () => void;
}

const UpdateAssessmentRuns = ({ program, onClose, onSuccess, selectedGroup }: UpdateAssessmentRunsProps) => {
    const { t } = useTranslation('programManagementPage');

    const handleSubmit: AssessmentRunsFormProps['onSubmit'] = async formData => {
        try {
            const variables: UpdateAssessmentRunsMutationVariables = {
                id: program.id,
                input: {
                    groupId: selectedGroup?.groupId || '',
                    assessmentRuns: formData.assessmentRuns,
                },
            };

            const { data } = await client.mutate({
                mutation: UpdateAssessmentRunsMutation,
                variables,
            });

            if (data?.updateAssessmentRuns?.__typename === 'UpdateAssessmentRunsSuccessfulResponse') {
                if (onSuccess) {
                    onSuccess();
                } else {
                    onClose();
                }
                showSuccessNotification({
                    message: t('assessmentRuns.notifications.updateSuccessTitle'),
                    description: t('assessmentRuns.notifications.updateSuccessDescription'),
                });
                return;
            }

            if (data?.updateAssessmentRuns?.__typename === 'ErrorResponse') {
                const error = {
                    fields: data.updateAssessmentRuns.fields,
                };
                showErrorNotification({
                    message: t('assessmentRuns.notifications.updateErrorTitle'),
                    description: error.fields?.[0]?.message || t('assessmentRuns.notifications.updateErrorDescription'),
                });
                throw error;
            }

            throw new Error('Unknown error updating assessment runs');
        } catch {
            throw new Error('An unexpected error occurred while updating assessment runs');
        }
    };
    return (
        <AssessmentRunsFormContainer
            mode="edit"
            onSubmit={handleSubmit}
            onClose={onClose}
            program={program}
            selectedGroup={selectedGroup}
        />
    );
};

export default UpdateAssessmentRuns;
